/**
 * Shop All multi-select Collections filter.
 * Merges product cards from selected collections (OR logic).
 */
class ShopAllCollectionFilter extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this._productListSelector = this.getAttribute("product-list") || "product-list";
    this._sectionId = this.getAttribute("section-id") || "";
    this._currentCollection = this.getAttribute("current-collection") || "";
    this._applying = false;

    this.syncChecksFromUrl();

    this.addEventListener("change", () => {
      this.applyFilters();
    });

    // Auto-apply only when URL has multi-collection params
    const urlSelected = new URL(window.location.href).searchParams.getAll("shop_collections");
    if (!window.__shopAllFilterBootstrapped && urlSelected.length > 0) {
      window.__shopAllFilterBootstrapped = true;
      this.applyFilters({ replaceHistory: false });
    }
  }

  syncChecksFromUrl() {
    const selected = new URL(window.location.href).searchParams.getAll("shop_collections");
    if (selected.length > 0) {
      this.querySelectorAll('input[name="shop_collections"]').forEach((input) => {
        input.checked = selected.includes(input.value);
      });
      return;
    }

    // Keep the current collection checked on its own shop page
    if (this._currentCollection && this._currentCollection !== "all") {
      this.querySelectorAll('input[name="shop_collections"]').forEach((input) => {
        input.checked = input.value === this._currentCollection;
      });
    }
  }

  get selectedHandles() {
    return Array.from(this.querySelectorAll('input[name="shop_collections"]:checked')).map(
      (input) => input.value
    );
  }

  get productList() {
    return document.getElementById(this._productListSelector) || document.querySelector("product-list");
  }

  get resultsCountEl() {
    return document.querySelector(".collection-toolbar__products-count");
  }

  get paginationEl() {
    const main = document.querySelector(".collection__main");
    if (!main) return null;
    return (
      main.querySelector("page-pagination") ||
      main.querySelector("nav.pagination") ||
      main.querySelector(".pagination") ||
      main.querySelector('nav[aria-label*="Pagination"], nav[aria-label*="pagination"]')
    );
  }

  previewParams(url) {
    const current = new URL(window.location.href);
    ["preview_theme_id", "key", "_ab", "_fd", "_sc"].forEach((key) => {
      const value = current.searchParams.get(key);
      if (value) url.searchParams.set(key, value);
    });
    return url;
  }

  syncAllRoots(handles) {
    document.querySelectorAll("shop-all-collection-filter").forEach((root) => {
      root.querySelectorAll('input[name="shop_collections"]').forEach((input) => {
        input.checked = handles.includes(input.value);
      });
    });
  }

  async applyFilters({ replaceHistory = true } = {}) {
    if (this._applying || window.__shopAllFilterApplying) return;
    this._applying = true;
    window.__shopAllFilterApplying = true;

    const handles = this.selectedHandles;
    const url = new URL(window.location.href);
    url.searchParams.delete("shop_collections");
    url.searchParams.delete("page");
    handles.forEach((handle) => url.searchParams.append("shop_collections", handle));

    if (replaceHistory) {
      history.replaceState({}, "", url.toString());
    }

    this.syncAllRoots(handles);

    const productList = this.productList;
    if (!productList) {
      this._applying = false;
      window.__shopAllFilterApplying = false;
      return;
    }

    // No selection, or only the current collection on its own page → show native results
    const onlyCurrentCollection =
      handles.length === 1 &&
      this._currentCollection &&
      this._currentCollection !== "all" &&
      handles[0] === this._currentCollection;

    if (handles.length === 0 || onlyCurrentCollection) {
      await this.reloadUnfilteredSection();
      this._applying = false;
      window.__shopAllFilterApplying = false;
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const cards = await this.fetchMergedCards(handles);
      productList.replaceChildren(...cards);

      const pagination = this.paginationEl;
      if (pagination) {
        pagination.hidden = true;
        pagination.style.display = "none";
      }

      if (this.resultsCountEl) {
        this.resultsCountEl.textContent = cards.length === 1 ? "1 product" : `${cards.length} products`;
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      this._applying = false;
      window.__shopAllFilterApplying = false;
    }
  }

  async fetchCollectionPage(handle, page) {
    const cardsUrl = this.previewParams(new URL(`/collections/${handle}`, window.location.origin));
    cardsUrl.searchParams.set("view", "cards");
    cardsUrl.searchParams.set("page", String(page));

    let response = await fetch(cardsUrl.toString());
    let html = await response.text();
    let doc = new DOMParser().parseFromString(html, "text/html");
    let pageCards = Array.from(doc.querySelectorAll("product-card"));
    let pageInfo = doc.querySelector("[data-cards-pagination]");

    if (pageInfo || (pageCards.length > 0 && !doc.querySelector("product-list"))) {
      return {
        cards: pageCards,
        pages: Number(pageInfo?.getAttribute("data-pages") || "1") || 1,
      };
    }

    const fullUrl = this.previewParams(new URL(`/collections/${handle}`, window.location.origin));
    fullUrl.searchParams.set("page", String(page));
    response = await fetch(fullUrl.toString());
    html = await response.text();
    doc = new DOMParser().parseFromString(html, "text/html");
    pageCards = Array.from(doc.querySelectorAll("product-list product-card"));

    let pages = 1;
    const lastPageMatch = html.match(/[?&]page=(\d+)/g);
    if (lastPageMatch) {
      pages = Math.max(...lastPageMatch.map((part) => Number(part.replace(/.*page=/, "")) || 1));
    }

    return { cards: pageCards, pages: pages || 1 };
  }

  async fetchMergedCards(handles) {
    const seen = new Set();
    const cards = [];

    for (const handle of handles) {
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages && page <= 40) {
        const { cards: pageCards, pages } = await this.fetchCollectionPage(handle, page);
        totalPages = pages || totalPages;

        pageCards.forEach((card) => {
          const key = card.getAttribute("handle") || card.querySelector("a")?.getAttribute("href") || "";
          if (!key || seen.has(key)) return;
          seen.add(key);
          cards.push(document.importNode(card, true));
        });

        if (pageCards.length === 0) break;
        page += 1;
      }
    }

    return cards;
  }

  async reloadUnfilteredSection() {
    if (!this._sectionId) {
      const url = this.previewParams(new URL("/collections/all", window.location.origin));
      window.location.href = url.toString();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    try {
      const url = this.previewParams(new URL(window.location.href));
      url.searchParams.delete("shop_collections");
      url.searchParams.delete("page");
      url.searchParams.set("section_id", this._sectionId);

      const clean = new URL(url);
      clean.searchParams.delete("section_id");
      history.replaceState({}, "", clean.toString());

      const response = await fetch(url.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newSection = doc.querySelector(".shopify-section");
      const currentSection = document.getElementById(`shopify-section-${this._sectionId}`);
      if (newSection && currentSection) {
        window.__shopAllFilterBootstrapped = false;
        currentSection.replaceChildren(...document.importNode(newSection, true).childNodes);
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
}

if (!customElements.get("shop-all-collection-filter")) {
  customElements.define("shop-all-collection-filter", ShopAllCollectionFilter);
}
